import { Injectable } from '@angular/core';
import { BcSettingsService } from 'common/settings/settings.service';
import { Settings } from 'common/settings/settings.interface';
import { SmartAdvisorWrapper, SessionParams, SmartAdvisorWrapperOptions } from 'shared/components/nanorep-panel/nanorep-sdk';
import { NotificationType } from 'common/common/constants/notification-type.enum';
import { NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { LaneManagerService } from 'shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';

@Injectable()
export class SmartAdvisorService {
  instance: SmartAdvisorWrapper;

  constructor(
    private _bcSettingsService: BcSettingsService,
    private _bcNotificationService: NotificationBarService,
    private _rxEmitter: RxEmitter
  ) {}

  async createWidget(options: SmartAdvisorWrapperOptions, session: SessionParams, node): Promise<SmartAdvisorWrapper> {
    if (!this.instance) {
      this.instance = new window.nanorep.sdk.SmartAdvisor(options, session, node);
    } else {
      await this.tearDown();
      this.setParams(options, session);
      this.instance.mountToNode(node);
    }

    return this.instance;
  }

  setParams(options: SmartAdvisorWrapperOptions, sessionParams: SessionParams) {
    this.instance.setParams(options, sessionParams);
  }

  copySelected(article: string, workItemId: string): void {
    this._rxEmitter.send(
      LaneManagerService.generateWorkItemObservableNamespace(
        MessageBusEvents.SendCannedMessageToEditor,
        workItemId
      ),
      article
    );
  }

  showSuggestionSentMessage() {
    this._bcNotificationService.addNotification({ id: 'suggestion-result', content: 'bcChatMessages.articleWasSent', type: NotificationType.Success });
  }

  showSuggestionErrorMessage() {
    this._bcNotificationService.addNotification({ id: 'suggestion-result', content: 'bcChatMessages.articleSuggestionError' });
  }

  mountToNode(node) {
    this.instance.mountToNode(node);
  }

  async getSmartAdvisorVersionValue(): Promise<boolean> {
    const settings: Settings = await this._bcSettingsService.getSettings();
    return this._bcSettingsService.ChatFeatures.isEnabled(settings, 'lc_smart_advisor_2_0');
  }

  async suggestArticle({ agentMessage, visitorMessage }) {
    const smartAdvisor = await this.instance.whenAppReady();
    return smartAdvisor.suggestArticle({ title: visitorMessage, content: agentMessage });
  }

  async ask(query: string) {
    const smartAdvisor = await this.instance.whenAppReady();
    smartAdvisor.ask(query);
  }

  async wrapUp(_chatId: string) {
    const smartAdvisor = await this.instance.whenAppReady();
    smartAdvisor.setWrapUp();
  }

  async isWrapUpActive(): Promise<boolean> {
    const smartAdvisor = await this.instance.whenAppReady();
    return smartAdvisor.isWrapUpActive();
  }

  async tearDown() {
    const smartAdvisor = await this.instance.whenAppReady();
    smartAdvisor.tearDown();
  }

  async updateScrollableHeight(height: number) {
    const smartAdvisorWidget = await this.instance.whenAppReady();
    smartAdvisorWidget.setHeight(height);
  }
}
